import React, { Suspense, useRef, useMemo, useEffect, createElement as h } from "react";
import { Canvas, useFrame, useThree } from "@react-three/fiber";
import { OrbitControls, Stars } from "@react-three/drei";
import * as THREE from "three";

// ──────────────────────────────────────────────────────────────
// Helpers
// ──────────────────────────────────────────────────────────────
const EARTH_R = 1.6;

const latLonToVec3 = (lat, lon, r = EARTH_R) => {
  const phi = (90 - lat) * (Math.PI / 180);
  const theta = (lon + 180) * (Math.PI / 180);
  return new THREE.Vector3(
    -r * Math.sin(phi) * Math.cos(theta),
    r * Math.cos(phi),
    r * Math.sin(phi) * Math.sin(theta),
  );
};

// Deterministic PRNG (Mulberry32)
function mulberry32(seed) {
  let s = seed >>> 0;
  return function () {
    s = (s + 0x6D2B79F5) >>> 0;
    let t = s;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

const ROUTE_PALETTE = [
  "#00D4FF", "#62FFB0", "#A077FF", "#FF6FA0",
  "#FFD27A", "#7BE6E8", "#4FC1FF", "#FF8AE2",
  "#A0FFD4", "#FFB066", "#4D6BFF", "#00FFD8",
];

// Build a curved arc from origin (on globe surface) to destination (off-globe orbit)
// Returns an array of THREE.Vector3 points.
function buildArc(originVec, destVec, segments = 48) {
  const mid = originVec.clone().add(destVec).multiplyScalar(0.5);
  // Lift the mid-point outward to bend the arc above the surface
  const lift = 0.6 + originVec.distanceTo(destVec) * 0.18;
  mid.normalize().multiplyScalar(EARTH_R + lift);
  const curve = new THREE.QuadraticBezierCurve3(originVec, mid, destVec);
  return curve.getPoints(segments);
}

// ──────────────────────────────────────────────────────────────
// Globe with grain + atmosphere
// ──────────────────────────────────────────────────────────────
function Globe() {
  const surfaceRef = useRef();
  const wireRef = useRef();
  useFrame((_, dt) => {
    if (surfaceRef.current) surfaceRef.current.rotation.y += dt * 0.04;
    if (wireRef.current) wireRef.current.rotation.y -= dt * 0.012;
  });
  return h(
    "group",
    null,
    // Solid surface
    h(
      "mesh",
      { ref: surfaceRef },
      h("sphereGeometry", { args: [EARTH_R, 96, 96] }),
      h("meshStandardMaterial", {
        color: "#03101c",
        emissive: "#053047",
        emissiveIntensity: 0.45,
        roughness: 0.92,
        metalness: 0.1,
      }),
    ),
    // Inner cyan wireframe
    h(
      "mesh",
      { ref: wireRef },
      h("sphereGeometry", { args: [EARTH_R + 0.004, 48, 48] }),
      h("meshBasicMaterial", {
        color: "#00D4FF",
        wireframe: true,
        transparent: true,
        opacity: 0.18,
      }),
    ),
    // Atmospheric halo
    h(
      "mesh",
      null,
      h("sphereGeometry", { args: [EARTH_R + 0.12, 64, 64] }),
      h("meshBasicMaterial", {
        color: "#00D4FF",
        transparent: true,
        opacity: 0.06,
        side: THREE.BackSide,
      }),
    ),
  );
}

// ──────────────────────────────────────────────────────────────
// Origin pulse pins (real country missions from backend)
// ──────────────────────────────────────────────────────────────
function OriginPins({ points }) {
  const ref = useRef();
  const positions = useMemo(() => {
    return points.map((p) => latLonToVec3(p.lat, p.lng, EARTH_R + 0.01));
  }, [points]);
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.y += dt * 0.04;
  });
  return h(
    "group",
    { ref },
    ...positions.map((v, i) =>
      h(
        "mesh",
        { key: i, position: v.toArray() },
        h("sphereGeometry", { args: [0.02, 12, 12] }),
        h("meshBasicMaterial", { color: points[i].active ? "#62FFB0" : "#00D4FF" }),
      ),
    ),
  );
}

// ──────────────────────────────────────────────────────────────
// Hundreds of simulated routes — arcs + traveling signal heads
// ──────────────────────────────────────────────────────────────
function RouteSwarm({ routes }) {
  // Per-route LineSegments are heavy; instead build a single merged buffer for the arcs
  // and a separate Points cloud for the moving signal heads.
  const groupRef = useRef();
  const arcsGeometryRef = useRef();
  const arcsMaterialRef = useRef();
  const headPointsRef = useRef();

  // Static buffer of all arc segments + per-vertex color
  const arcsData = useMemo(() => {
    const positions = [];
    const colors = [];
    routes.forEach((r) => {
      const c = new THREE.Color(r.color);
      for (let i = 0; i < r.points.length - 1; i++) {
        const a = r.points[i];
        const b = r.points[i + 1];
        // Fade along arc: head bright, tail dim
        const tA = i / (r.points.length - 1);
        const tB = (i + 1) / (r.points.length - 1);
        const alphaA = 0.25 + tA * 0.6;
        const alphaB = 0.25 + tB * 0.6;
        positions.push(a.x, a.y, a.z, b.x, b.y, b.z);
        colors.push(c.r * alphaA, c.g * alphaA, c.b * alphaA);
        colors.push(c.r * alphaB, c.g * alphaB, c.b * alphaB);
      }
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.Float32BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.Float32BufferAttribute(colors, 3));
    return g;
  }, [routes]);

  // Dynamic signal heads — one Point per route, updated each frame
  const headsData = useMemo(() => {
    const positions = new Float32Array(routes.length * 3);
    const colors = new Float32Array(routes.length * 3);
    routes.forEach((r, i) => {
      const c = new THREE.Color(r.color);
      colors[i * 3 + 0] = c.r;
      colors[i * 3 + 1] = c.g;
      colors[i * 3 + 2] = c.b;
      positions[i * 3 + 0] = r.points[0].x;
      positions[i * 3 + 1] = r.points[0].y;
      positions[i * 3 + 2] = r.points[0].z;
    });
    const g = new THREE.BufferGeometry();
    g.setAttribute("position", new THREE.BufferAttribute(positions, 3));
    g.setAttribute("color", new THREE.BufferAttribute(colors, 3));
    return g;
  }, [routes]);

  // Pre-cache curves so we can sample fast per frame
  const curves = useMemo(
    () => routes.map((r) => new THREE.CatmullRomCurve3(r.points, false)),
    [routes],
  );

  useFrame((state, dt) => {
    if (groupRef.current) groupRef.current.rotation.y += dt * 0.04;
    if (!headPointsRef.current) return;
    const posAttr = headPointsRef.current.geometry.attributes.position;
    const time = state.clock.elapsedTime;
    for (let i = 0; i < routes.length; i++) {
      const r = routes[i];
      // Each route has its own period (4-14s) and offset
      const t = ((time / r.period) + r.offset) % 1;
      const v = curves[i].getPointAt(t);
      posAttr.array[i * 3 + 0] = v.x;
      posAttr.array[i * 3 + 1] = v.y;
      posAttr.array[i * 3 + 2] = v.z;
    }
    posAttr.needsUpdate = true;
    if (arcsMaterialRef.current) {
      arcsMaterialRef.current.opacity = 0.55 + Math.sin(time * 0.8) * 0.06;
    }
  });

  return h(
    "group",
    { ref: groupRef },
    h(
      "lineSegments",
      { geometry: arcsData, ref: arcsGeometryRef },
      h("lineBasicMaterial", {
        ref: arcsMaterialRef,
        vertexColors: true,
        transparent: true,
        opacity: 0.6,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    ),
    h(
      "points",
      { geometry: headsData, ref: headPointsRef },
      h("pointsMaterial", {
        size: 0.08,
        vertexColors: true,
        sizeAttenuation: true,
        transparent: true,
        opacity: 1.0,
        depthWrite: false,
        blending: THREE.AdditiveBlending,
      }),
    ),
  );
}

// Slow rotating outer ring (mission relay halo)
function RelayRing() {
  const ref = useRef();
  useFrame((_, dt) => {
    if (ref.current) ref.current.rotation.z += dt * 0.05;
  });
  return h(
    "mesh",
    { ref, rotation: [Math.PI / 2.3, 0, 0] },
    h("ringGeometry", { args: [3.4, 3.46, 128] }),
    h("meshBasicMaterial", {
      color: "#00D4FF",
      transparent: true,
      opacity: 0.28,
      side: THREE.DoubleSide,
      blending: THREE.AdditiveBlending,
    }),
  );
}

// ──────────────────────────────────────────────────────────────
// Responsive camera rig — keeps the globe well-framed on narrow viewports.
// On portrait/mobile aspect, push camera back so the sphere fills the frame.
// ──────────────────────────────────────────────────────────────
function ResponsiveCameraRig() {
  const { camera, size } = useThree();
  useEffect(() => {
    const aspect = size.width / Math.max(1, size.height);
    if (aspect < 1.3) {
      // Portrait or near-square — frame the globe to fill ~75% of the canvas
      camera.position.set(0, 0, 4.5);
      camera.lookAt(0, 0, 0);
      if ("fov" in camera) { camera.fov = 55; camera.updateProjectionMatrix(); }
    } else {
      // Wide desktop layout
      camera.position.set(0, 0.4, 5.4);
      camera.lookAt(0, 0, 0);
      if ("fov" in camera) { camera.fov = 45; camera.updateProjectionMatrix(); }
    }
  }, [camera, size.width, size.height]);
  return null;
}

// ──────────────────────────────────────────────────────────────
// Build all routes (deterministic so it doesn't shuffle each frame)
// ──────────────────────────────────────────────────────────────
function useRoutes(points, count) {
  return useMemo(() => {
    const rnd = mulberry32(91174);
    const list = [];
    const originPool = points.length > 0
      ? points
      : [
          { lat: 0, lng: 0 },
          { lat: 40, lng: -100 },
          { lat: -23, lng: -47 },
        ];
    for (let i = 0; i < count; i++) {
      const o = originPool[Math.floor(rnd() * originPool.length)];
      const originVec = latLonToVec3(o.lat, o.lng, EARTH_R + 0.005);
      // Destination orbits around Earth at random radii / angles
      const destR = 2.2 + rnd() * 1.6;
      const dPhi = rnd() * Math.PI * 2;
      const dTheta = (rnd() - 0.5) * Math.PI * 0.9;
      const destVec = new THREE.Vector3(
        destR * Math.cos(dTheta) * Math.cos(dPhi),
        destR * Math.sin(dTheta),
        destR * Math.cos(dTheta) * Math.sin(dPhi),
      );
      list.push({
        id: i,
        points: buildArc(originVec, destVec, 40),
        color: ROUTE_PALETTE[Math.floor(rnd() * ROUTE_PALETTE.length)],
        period: 4 + rnd() * 10,
        offset: rnd(),
      });
    }
    return list;
  }, [points, count]);
}

// ──────────────────────────────────────────────────────────────
// Scene
// ──────────────────────────────────────────────────────────────
function Scene({ points, routeCount }) {
  const routes = useRoutes(points, routeCount);
  return h(
    Suspense,
    { fallback: null },
    h(ResponsiveCameraRig, null),
    h("color", { attach: "background", args: ["#020409"] }),
    h("ambientLight", { intensity: 0.35 }),
    h("pointLight", { position: [6, 4, 6], intensity: 1.2, color: "#66E9FF" }),
    h("pointLight", { position: [-5, -3, -4], intensity: 0.4, color: "#FF6FA0" }),
    h(Stars, { radius: 80, depth: 50, count: 6000, factor: 2.4, fade: true, speed: 0.4 }),
    h(Globe, null),
    h(OriginPins, { points }),
    h(RouteSwarm, { routes }),
    h(RelayRing, null),
    h(OrbitControls, {
      enablePan: false,
      enableZoom: true,
      minDistance: 2.6,
      maxDistance: 8.5,
      target: [0, 0, 0],
      autoRotate: true,
      autoRotateSpeed: 0.35,
    }),
  );
}

export default function Globe3D({ points = [], routeCount = 280, className = "" }) {
  return (
    <div className={className} style={{ position: "relative", width: "100%", height: "100%" }}>
      <Canvas camera={{ position: [0, 0, 5], fov: 50 }} dpr={[1, 2]} style={{ width: "100%", height: "100%" }}>
        <Scene points={points} routeCount={routeCount} />
      </Canvas>
    </div>
  );
}
